// Translates raw ingest / Firecrawl / Supabase error strings into a short, human
// message plus an optional hint. Used by the watch-rules UI to avoid surfacing
// raw "Edge Function returned a non-2xx status code" toasts.

export type FriendlyError = { message: string; hint?: string; raw?: string };

export function friendlyError(raw: unknown): FriendlyError {
  const text = typeof raw === "string" ? raw : (raw as any)?.message ?? JSON.stringify(raw ?? "");
  const lower = text.toLowerCase();

  // Firecrawl proxy/tunnel failures (the BoE Bank Rate case)
  if (lower.includes("err_tunnel_connection_failed") || lower.includes("scrape_site_error")) {
    return {
      message: "Firecrawl couldn't reach the target site (proxy tunnel failed).",
      hint: "Usually transient — retry in a few minutes. If it keeps failing, the page may block scrapers; try a different URL.",
      raw: text,
    };
  }
  if (lower.includes("firecrawl_scrape_402") || lower.includes("insufficient credits")) {
    return { message: "Firecrawl is out of credits.", hint: "Top up the Firecrawl account, then retry.", raw: text };
  }
  if (lower.includes("firecrawl_scrape_429") || lower.includes("rate limit")) {
    return { message: "Firecrawl rate limit hit.", hint: "Wait a minute and retry.", raw: text };
  }
  if (lower.match(/firecrawl_scrape_5\d\d/) || lower.includes("firecrawl_500")) {
    return { message: "Firecrawl returned a server error.", hint: "Usually transient — retry shortly.", raw: text };
  }
  if (lower.includes("firecrawl_scrape_4")) {
    return { message: "Firecrawl rejected the request (bad URL or page blocks scraping).", hint: "Check the URL works in a browser and isn't behind auth.", raw: text };
  }
  if (lower.includes("empty_scrape_result")) {
    return { message: "Scrape returned no meaningful content.", hint: "The page may be JS-rendered behind auth, or the URL is wrong.", raw: text };
  }
  if (lower.includes("fred_") ) {
    return { message: "FRED API returned an error.", hint: "Check the series ID(s) and FRED_API_KEY.", raw: text };
  }
  if (lower.includes("non-2xx")) {
    return { message: "The ingest function failed.", hint: "Open the rule to see the full last_result.", raw: text };
  }
  if (lower.includes("rule_not_found")) {
    return { message: "Watch rule not found.", raw: text };
  }
  // Default — strip noisy prefixes
  const cleaned = text.replace(/^ingest_\d+:\s*/, "").replace(/^firecrawl_scrape_\d+:\s*/, "").slice(0, 200);
  return { message: cleaned || "Unknown error", raw: text };
}
